import { useState, useEffect } from 'react';
import { FlowNode } from '../../types';

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

// Grouped because the two halves do fundamentally different things, and "base64" appearing in
// both this list and the encoding list below invites confusing one for the other.
const HASH_ALGORITHMS = ['sha256', 'hmac-sha256'] as const;
const ENCODE_ALGORITHMS = ['base64', 'base64url'] as const;
const ENCODINGS = ['hex', 'base64', 'base64url'] as const;
const NORMALIZERS = ['', 'lower', 'trim', 'lower+trim'] as const;

const ALGORITHM_HELP: Record<string, string> = {
  'sha256': 'One-way fingerprint, no key. Anyone can recompute it — that is how hashed emails are matched.',
  'hmac-sha256': 'One-way fingerprint with a secret key. Only someone holding the key can produce it, which is what proves a webhook is genuine.',
  'base64': 'Not a hash: re-encodes the input and can be decoded back. For APIs that want a value base64-encoded, never to protect data.',
  'base64url': 'Like base64, but with -_ instead of +/ and no = padding, so it is safe inside a URL.',
};

const INPUT_CLASS = 'w-full border border-input rounded p-1.5 text-xs font-mono bg-background';

export function CryptoStepConfig({ node, onUpdate }: Props) {
  const [algorithm, setAlgorithm] = useState((node.config?.algorithm as string) ?? 'sha256');
  const [input, setInput] = useState((node.config?.input as string) ?? '');
  const [key, setKey] = useState((node.config?.key as string) ?? '');
  const [encoding, setEncoding] = useState((node.config?.encoding as string) ?? 'hex');
  const [normalize, setNormalize] = useState((node.config?.normalize as string) ?? '');
  const [alias, setAlias] = useState((node.config?.alias as string) ?? '');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setAlgorithm((node.config?.algorithm as string) ?? 'sha256');
    setInput((node.config?.input as string) ?? '');
    setKey((node.config?.key as string) ?? '');
    setEncoding((node.config?.encoding as string) ?? 'hex');
    setNormalize((node.config?.normalize as string) ?? '');
    setAlias((node.config?.alias as string) ?? '');
  }, [node.id]);

  const needsKey = algorithm === 'hmac-sha256';
  const isHash = algorithm === 'sha256' || algorithm === 'hmac-sha256';

  useEffect(() => {
    const next: Record<string, unknown> = { ...node.config, algorithm, input };

    if (needsKey) next.key = key;
    else delete next.key;

    if (isHash) next.encoding = encoding;
    else delete next.encoding;

    if (normalize) next.normalize = normalize;
    else delete next.normalize;

    if (alias.trim()) next.alias = alias.trim();
    else delete next.alias;

    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithm, input, key, encoding, normalize, alias]);

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-muted-foreground block mb-1">Algorithm</label>
        <select
          className={INPUT_CLASS}
          value={algorithm}
          onChange={e => setAlgorithm(e.target.value)}
        >
          <optgroup label="Hash — one-way, cannot be reversed">
            {HASH_ALGORITHMS.map(a => <option key={a} value={a}>{a}</option>)}
          </optgroup>
          <optgroup label="Encode only — reversible, not protection">
            {ENCODE_ALGORITHMS.map(a => <option key={a} value={a}>{a}</option>)}
          </optgroup>
        </select>
        <p className="text-xs text-muted-foreground mt-1">{ALGORITHM_HELP[algorithm]}</p>
      </div>

      <div>
        <label className="text-xs text-muted-foreground block mb-1">Input</label>
        <input
          type="text"
          className={INPUT_CLASS}
          placeholder="{{body.email}}"
          value={input}
          onChange={e => setInput(e.target.value)}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Use <span className="font-mono">{'{{rawBody}}'}</span> to verify an inbound webhook
          signature — it is computed over the exact bytes received.
        </p>
      </div>

      {needsKey && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Key</label>
          <input
            type="text"
            className={INPUT_CLASS}
            placeholder="{{env.STRIPE_WEBHOOK_SECRET}}"
            value={key}
            onChange={e => setKey(e.target.value)}
          />
        </div>
      )}

      {isHash && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">
            How to write the hash as text
          </label>
          <select
            className={INPUT_CLASS}
            value={encoding}
            onChange={e => setEncoding(e.target.value)}
          >
            {ENCODINGS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            The hash is always 32 bytes; this only changes how they are spelled — same value, three
            notations. Pick whatever the other side expects: Stripe compares hex, HubSpot base64,
            the ads APIs lowercase hex.
          </p>
        </div>
      )}

      <div>
        <label className="text-xs text-muted-foreground block mb-1">Normalize input</label>
        <select
          className={INPUT_CLASS}
          value={normalize}
          onChange={e => setNormalize(e.target.value)}
        >
          {NORMALIZERS.map(n => (
            <option key={n || 'none'} value={n}>{n || 'none'}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          Applied before hashing. LinkedIn, Google and Meta expect a lowercased, trimmed email —
          a different spelling hashes to something else entirely and simply never matches, without
          any error.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Step alias</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. hash-email"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
        <p className="text-xs text-muted-foreground">
          Result is available as{' '}
          <span className="font-mono text-primary">{`{{${alias || node.id}.value}}`}</span>
        </p>
      </div>
    </div>
  );
}
